import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Alert from '../components/common/Alert';
import PasswordField from '../components/forms/PasswordField';
import PhraseInputs from '../components/forms/PhraseInputs';
import { isValidEmail, validateLoginForm } from '../utils/validators';
import { useAuth } from '../contexts/AuthContext';
import { AUTH_ENDPOINTS } from '../utils/constants';

import '../styles/global.css';
import '../styles/components/login.css';

// Constants

const REDIRECT_DELAY = 5000;
const ALERT_DURATION = 5000;

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [useSecretPhrases, setUseSecretPhrases] = useState(false);
  const [phrases, setPhrases] = useState(Array(12).fill(''));
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState({
    isOpen: false,
    title: '',
    message: '',
    footer: '',
    type: 'success'
  });

  const handlePhraseChange = (index, value) => {
  const newPhrases = [...phrases];
  newPhrases[index] = value.toLowerCase().trim();
  setPhrases(newPhrases);
};

  const toggleLoginMethod = () => {
    setUseSecretPhrases(!useSecretPhrases);
    // Clear inputs on toggle
    setEmail('');
    setPassword('');
    setPhrases(Array(12).fill(''));
  };

  const closeAlert = () => {
    setAlert({ ...alert, isOpen: false });
  };

  const showAlert = (title, message, type = 'success', footer = '') => {
    setAlert({
      isOpen: true,
      title,
      message,
      footer,
      type
    });

    // Auto close alert after duration if not a success message that leads to redirect
    if (!(type === 'success' && !message.includes('overdue'))) {
      setTimeout(() => {
        closeAlert();
      }, ALERT_DURATION);
    }
  };

  const validateInputs = () => {
    if (useSecretPhrases) {
      // Validate phrases
      const emptyPhrases = phrases.filter(phrase => phrase === '');
      if (emptyPhrases.length > 0) {
        showAlert(
          'Input Error',
          `Please fill in all secret phrases. ${emptyPhrases.length} phrase(s) are empty.`,
          'fail',
          'All 12 secret phrases are required for login.'
        );
        return false;
      }
    } else {
      // Validate email and password
      if (email === '' || password === '') {
        showAlert(
          'Input Error',
          'Please fill in both the email and password fields.',
          'fail',
          'Both fields are required for login.'
        );
        return false;
      }

      if (!isValidEmail(email)) {
        showAlert(
          'Invalid Email',
          'Please enter a valid email address.',
          'fail',
          'The email format is incorrect.'
        );
        return false;
      }

      if (password.length < 8) {
        showAlert(
          'Invalid Password',
          'Password must be at least 8 characters long.',
          'fail',
          'Please enter a valid password.'
        );
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateInputs()) {
      return;
    }

    setIsLoading(true);

    try {
      let result;

      if (useSecretPhrases) {
        result = await loginUserWithPhrases(phrases);
      } else {
        result = await loginUser(email, password);
      }

      if (result && result.success) {
        handleLoginSuccess(result);
      }
    } catch (error) {
      handleUnexpectedError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const loginUser = async (email, password) => {
    const data = { email, password };

    try {
      const response = await fetch(AUTH_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        credentials: 'include'
      });

      const result = await response.json();

      if (!response.ok) {
        handleLoginError(result.error || 'Unknown error');
        return null;
      }

      if (!result.success) {
        handleLoginFailure(result.message || 'Unknown error');
        return null;
      }

      return result;
    } catch (error) {
      handleUnexpectedError(error);
      return null;
    }
  };

  const loginUserWithPhrases = async (phrases) => {
    const data = { phrases };

    try {
      const response = await fetch(AUTH_ENDPOINTS.LOGIN_WITH_PHRASES, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        credentials: 'include'
      });

      const result = await response.json();

      if (!response.ok) {
        handleLoginError(result.error || 'Unknown error');
        return null;
      }

      if (!result.success) {
        handleLoginFailure(result.message || 'Unknown error');
        return null;
      }

      return result;
    } catch (error) {
      handleUnexpectedError(error);
      return null;
    }
  };

  const handleLoginSuccess = (result) => {
    storeUserSession(result);

    // Call the onLoginSuccess prop with the token
    login(result);

    if (result.userDataResponse.isPaymentOverdue) {
      const deletionDate = new Date(result.userDataResponse.dataDeletionDate);
      const formattedDeletionDate = deletionDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      // Create and show the warning alert
      const header = 'Account Frozen - Payment Required';
      const body = 'Your account has been frozen due to overdue payment. Account restrictions are now in place:';
      const footer = `
      • You can only view and remove existing data
      • Adding new data is disabled
      • All data will be permanently deleted on ${formattedDeletionDate} if payment is not received
      • Please visit "My Profile > Account" section to make your payment and restore full access`;

      showAlert(header, body, 'fail', footer);

      // Set up event handler for alert dismissal
      const handleAlertClose = () => {
        redirectToDashboard();
      };

      // Add event listener for alert dismissal
      const timerId = setTimeout(() => {
        redirectToDashboard();
      }, REDIRECT_DELAY);

      return () => clearTimeout(timerId);
    } else {

        redirectToDashboard();

    }
  };

  const storeUserSession = (result) => {
    sessionStorage.setItem('isLoggedIn', 'true');
    sessionStorage.setItem('userDetails', JSON.stringify(result.userDataResponse));
    sessionStorage.setItem('userToken', result.token);
    sessionStorage.setItem('accountStatus', result.userDataResponse.accountStatus);
  };

  const redirectToDashboard = () => {
    navigate('/dashboard');
  };

  const handleLoginError = (error) => {
    showAlert(
      'Login Error',
      error || 'An error occurred during login. Please try again.',
      'fail',
      'If the problem persists, please contact support.'
    );
  };

  const handleLoginFailure = (message) => {
    showAlert(
      'Login Failed',
      message || 'Unable to log in with provided credentials.',
      'fail',
      'Please check your email and password/phrases and try again.'
    );
  };

  const handleUnexpectedError = (error) => {
    console.error('Error logging in:', error);
    showAlert(
      'Unexpected Error',
      'An unexpected error occurred during the login process.',
      'fail',
      'Please try again later or contact support if the issue persists.'
    );
  };

  return (
    <>
      <Navbar />

      <div className="main-container">
        <div className="login-card">
          <div className="card-header">
            <h1>Login</h1>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              {!useSecretPhrases ? (
                <>
                  <div className="form-group">
                    <input
                      type="email"
                      className="form-control"
                      id="loginEmail"
                      placeholder="Email Address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <PasswordField
                    id="loginPassword"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </>
              ) : (
                <PhraseInputs
                  phrases={phrases}
                  onChange={handlePhraseChange}
                />
              )}

              <div className="form-group">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading
                    ? 'Logging in...'
                    : useSecretPhrases
                      ? 'Login with Secret Phrases'
                      : 'Login'
                  }
                </button>
              </div>
            </form>

            <div className="card-divider"></div>

            <div className="card-links">
              <Link to="/forgot-password" className="card-link">Forgot Password?</Link>
            </div>

            <div className="alternative-login">
              <button
                type="button"
                className="btn-link"
                onClick={toggleLoginMethod}
              >
                {useSecretPhrases
                  ? 'Login with Email and Password'
                  : 'Login with Secret Phrases'
                }
              </button>
            </div>

            <div className="create-account">
              <span className="text-light">Don't have an account?</span>
              <Link to="/register" className="card-link"> Create an Account</Link>
            </div>
          </div>
        </div>
      </div>

      <Alert
        isOpen={alert.isOpen}
        onClose={closeAlert}
        title={alert.title}
        message={alert.message}
        footer={alert.footer}
        type={alert.type}
      />
    </>
  );
};

export default Login;